import React, { useState } from "react";
import { X, Check, Star, ChartLineUp, ClockCounterClockwise, Lightning, Crown, CreditCard, CurrencyBtc, ArrowLeft, CaretRight, Spinner } from "phosphor-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "../lib/api";

// Payment method types
const PAYMENT_METHOD = {
  STRIPE: 'stripe',
  CRYPTO: 'crypto'
};

// BRL pricing for Stripe
const STRIPE_PRICES = {
  yearly: { amount: 442.00, formatted: "R$442,00" }
};

export default function ImportPaywallModal({ 
  isOpen, 
  onClose, 
  onUpgradeClick // Legacy callback for crypto flow
}) {
  const [step, setStep] = useState(1); // 1 = info, 2 = payment method, 3 = redirecting
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) return null;

  const handleContinue = () => {
    setStep(2);
  };

  const handleSelectMethod = (method) => {
    if (method === PAYMENT_METHOD.CRYPTO) {
      // Use legacy crypto flow
      onUpgradeClick();
    } else {
      // Stripe checkout
      handleStripeCheckout();
    }
  };

  const handleStripeCheckout = async () => {
    setIsLoading(true);
    try {
      const originUrl = window.location.origin;
      
      const response = await api.post('/payments/stripe/create-checkout-session', {
        plan_type: 'yearly',
        origin_url: originUrl
      });
      
      if (response.data.checkout_url) {
        // Show redirecting state
        setStep(3);
        // Small delay to show the redirecting message before navigating
        setTimeout(() => {
          window.location.href = response.data.checkout_url;
        }, 100);
      } else {
        throw new Error('No checkout URL received from server');
      }
    } catch (error) {
      console.error('[Stripe] Checkout error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to create checkout session';
      toast.error(errorMessage);
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setStep(1);
    setIsLoading(false);
  };

  // Step 3: Redirecting to Stripe
  if (step === 3) {
    return (
      <div 
        className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        data-testid="import-paywall-redirecting"
      >
        <div className="bg-[#121212] border border-white/10 rounded-sm p-8 w-full max-w-md text-center">
          <div className="w-16 h-16 bg-[#635BFF]/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Spinner size={32} weight="bold" className="text-[#635BFF] animate-spin" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Redirecting to checkout...</h2>
          <p className="text-white/60 text-sm">
            You'll be redirected to Stripe to complete your payment securely.
          </p>
        </div>
      </div>
    );
  }

  // Step 2: Payment Method Selection
  if (step === 2) {
    return (
      <div 
        className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={(e) => !isLoading && e.target === e.currentTarget && onClose()}
        data-testid="import-paywall-modal"
      >
        <div className="bg-[#121212] border border-white/10 rounded-sm w-full max-w-md overflow-hidden relative">
          {/* Back button */}
          <button
            onClick={handleBack}
            disabled={isLoading}
            className="absolute top-4 left-4 text-white/40 hover:text-white p-1 disabled:opacity-50"
          >
            <ArrowLeft size={20} />
          </button>

          {/* Close button */}
          <button
            onClick={onClose}
            disabled={isLoading}
            className="absolute top-4 right-4 text-white/40 hover:text-white p-1 disabled:opacity-50"
          >
            <X size={20} />
          </button>

          {/* Header */}
          <div className="p-6 border-b border-white/10">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-[#FFD700]/20 to-[#FFA500]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Crown size={32} weight="fill" className="text-[#FFD700]" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Choose Payment Method</h2>
              <p className="text-white/50 text-sm">
                Select how you'd like to pay for PRO Yearly
              </p>
            </div>
          </div>

          {/* Payment Method Options */}
          <div className="p-6 space-y-3">
            {/* Stripe Option */}
            <button
              onClick={() => handleSelectMethod(PAYMENT_METHOD.STRIPE)}
              disabled={isLoading}
              className="w-full bg-[#0A0A0A] border border-white/10 hover:border-[#635BFF]/50 rounded-sm p-4 flex items-center justify-between transition-all group disabled:opacity-50"
              data-testid="payment-method-stripe"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#635BFF]/10 rounded-full flex items-center justify-center">
                  <CreditCard size={24} weight="duotone" className="text-[#635BFF]" />
                </div>
                <div className="text-left">
                  <div className="text-white font-semibold flex items-center gap-2">
                    Card / PIX
                    <span className="text-[10px] bg-[#635BFF]/20 text-[#635BFF] px-2 py-0.5 rounded-full font-bold">
                      BRL
                    </span>
                  </div>
                  <div className="text-white/50 text-sm">{STRIPE_PRICES.yearly.formatted}/year</div>
                </div>
              </div>
              {isLoading ? (
                <Spinner size={20} className="text-[#635BFF] animate-spin" />
              ) : (
                <CaretRight size={20} className="text-white/30 group-hover:text-white/60 transition-colors" />
              )}
            </button>

            {/* Crypto Option */}
            <button
              onClick={() => handleSelectMethod(PAYMENT_METHOD.CRYPTO)}
              disabled={isLoading}
              className="w-full bg-[#0A0A0A] border border-white/10 hover:border-[#F0B90B]/50 rounded-sm p-4 flex items-center justify-between transition-all group disabled:opacity-50"
              data-testid="payment-method-crypto"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#F0B90B]/10 rounded-full flex items-center justify-center">
                  <CurrencyBtc size={24} weight="duotone" className="text-[#F0B90B]" />
                </div>
                <div className="text-left">
                  <div className="text-white font-semibold flex items-center gap-2">
                    Crypto
                    <span className="text-[10px] bg-[#F0B90B]/20 text-[#F0B90B] px-2 py-0.5 rounded-full font-bold">
                      USDT
                    </span>
                  </div>
                  <div className="text-white/50 text-sm">79 USDT/year (BEP20)</div>
                </div>
              </div>
              <CaretRight size={20} className="text-white/30 group-hover:text-white/60 transition-colors" />
            </button>
          </div>

          {/* Info text */}
          <div className="px-6 pb-6">
            <p className="text-center text-white/40 text-xs">
              Secure payment processing. Your account will be upgraded instantly.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Step 1: Info & Features
  return (
    <div 
      className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="import-paywall-modal"
    >
      <div className="bg-[#121212] border border-white/10 rounded-sm w-full max-w-md overflow-hidden relative">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white/40 hover:text-white p-1 z-10"
        >
          <X size={20} />
        </button>

        {/* Header */}
        <div className="p-6 border-b border-white/10">
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-[#FFD700]/20 to-[#FFA500]/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Crown size={32} weight="fill" className="text-[#FFD700]" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Upgrade to PRO Yearly</h2>
            <p className="text-white/50 text-sm">
              Import your full trading history and unlock advanced analytics.
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5">
          {/* Features List */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-[#00E676]/10 rounded-full flex items-center justify-center shrink-0">
                <ClockCounterClockwise size={16} className="text-[#00E676]" />
              </div>
              <span className="text-white/80 text-sm">Unlimited historical data import</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-[#00E676]/10 rounded-full flex items-center justify-center shrink-0">
                <ChartLineUp size={16} className="text-[#00E676]" />
              </div>
              <span className="text-white/80 text-sm">Advanced performance insights</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-[#00E676]/10 rounded-full flex items-center justify-center shrink-0">
                <Star size={16} className="text-[#00E676]" />
              </div>
              <span className="text-white/80 text-sm">Long-term trading analysis</span>
            </div>
          </div>

          {/* Plan Summary Card */}
          <div className="bg-gradient-to-b from-[#FFD700]/10 to-[#0A0A0A] border-2 border-[#FFD700] rounded-sm p-5 relative">
            <div className="absolute -top-3 right-4 bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-black text-xs font-bold px-3 py-1 rounded-full">
              BEST VALUE
            </div>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-white font-bold text-lg flex items-center gap-2">
                  Pro Yearly
                  <Star size={16} weight="fill" className="text-[#FFD700]" />
                </div>
                <div className="text-white/50 text-sm">Full access for 12 months</div>
              </div>
              <div className="text-right">
                <div className="text-[#00E676] text-xs font-medium">~3 months free!</div>
              </div>
            </div>
            
            {/* Yearly Benefits */}
            <div className="mt-4 pt-4 border-t border-[#FFD700]/20 space-y-2">
              <div className="flex items-center gap-2 text-sm text-white/70">
                <Check size={14} className="text-[#00E676]" />
                <span>Track every trade without limits</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-white/70">
                <Check size={14} className="text-[#00E676]" />
                <span>Behavioral insights on your patterns</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-white/70">
                <Lightning size={14} weight="fill" className="text-[#FFD700]" />
                <span>Import unlimited historical data</span>
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <Button
            onClick={handleContinue}
            className="w-full bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-black font-bold hover:opacity-90 h-12"
            data-testid="import-paywall-upgrade-btn"
          >
            Upgrade to PRO Yearly
          </Button>
          
          <p className="text-white/40 text-xs text-center">
            Pay with Card, PIX or Crypto. Your account will be upgraded after payment.
          </p>
        </div>
      </div>
    </div>
  );
}
